import { useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { cn } from '@/lib/utils';

const controlSchema = z.object({
  control_id: z.string().min(1, 'Control ID is required'),
  title: z.string().min(1, 'Title is required'),
  description: z.string().optional(),
  domain: z.string().min(1, 'Domain is required'),
  standard_key: z.string().min(1, 'Standard is required'),
  status: z.enum(['implemented', 'not_implemented', 'partially_implemented', 'not_applicable']),
  gap_statement: z.string().optional(),
  remediation: z.string().optional(),
  due_date: z.string().optional(),
});

type ControlFormData = z.infer<typeof controlSchema>;

interface ControlEditDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  control: any;
  onSave: (id: string, data: any) => void;
  isSaving?: boolean;
}

export function ControlEditDialog({ open, onOpenChange, control, onSave, isSaving }: ControlEditDialogProps) {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    setValue,
    watch,
  } = useForm<ControlFormData>({
    resolver: zodResolver(controlSchema),
    defaultValues: {
      status: 'not_implemented',
    },
  });

  useEffect(() => {
    if (control && open) {
      reset({
        control_id: control.control_id || '',
        title: control.title || '',
        description: control.description || '',
        domain: control.domain || '',
        standard_key: control.standard_key || '',
        status: control.status || 'not_implemented',
        gap_statement: control.gap_statement || '',
        remediation: control.remediation || '',
        due_date: control.due_date || '',
      });
    }
  }, [control, open, reset]);

  const dueDate = watch('due_date');

  const onSubmit = (data: ControlFormData) => {
    onSave(control.id, data);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Control</DialogTitle>
          <DialogDescription>
            Update control details and implementation status
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="control_id">Control ID *</Label>
              <Input
                id="control_id"
                {...register('control_id')}
                placeholder="e.g., AC-1"
              />
              {errors.control_id && (
                <p className="text-sm text-destructive mt-1">{errors.control_id.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="domain">Domain *</Label>
              <Input
                id="domain"
                {...register('domain')}
                placeholder="e.g., Access Control"
              />
              {errors.domain && (
                <p className="text-sm text-destructive mt-1">{errors.domain.message}</p>
              )}
            </div>
          </div>

          <div>
            <Label htmlFor="title">Control Title *</Label>
            <Input
              id="title"
              {...register('title')}
              placeholder="Enter control title"
            />
            {errors.title && (
              <p className="text-sm text-destructive mt-1">{errors.title.message}</p>
            )}
          </div>

          <div>
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              {...register('description')}
              placeholder="Detailed description of the control"
              className="min-h-[80px]"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="standard_key">Standard *</Label>
              <Select
                value={watch('standard_key')}
                onValueChange={(value) => setValue('standard_key', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select standard" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="iso27001">ISO 27001</SelectItem>
                  <SelectItem value="nist">NIST CSF</SelectItem>
                  <SelectItem value="iec62443">IEC 62443</SelectItem>
                  <SelectItem value="cis">CIS Controls</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
              {errors.standard_key && (
                <p className="text-sm text-destructive mt-1">{errors.standard_key.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={watch('status')}
                onValueChange={(value: any) => setValue('status', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="implemented">Implemented</SelectItem>
                  <SelectItem value="partially_implemented">Partially Implemented</SelectItem>
                  <SelectItem value="not_implemented">Not Implemented</SelectItem>
                  <SelectItem value="not_applicable">Not Applicable</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label htmlFor="gap_statement">Gap Statement</Label>
            <Textarea
              id="gap_statement"
              {...register('gap_statement')}
              placeholder="Describe any gaps in implementation"
              className="min-h-[80px]"
            />
          </div>

          <div>
            <Label htmlFor="remediation">Remediation Plan</Label>
            <Textarea
              id="remediation"
              {...register('remediation')}
              placeholder="Describe the remediation plan"
              className="min-h-[80px]"
            />
          </div>

          <div>
            <Label>Due Date</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className={cn(
                    "w-full justify-start text-left font-normal",
                    !dueDate && "text-muted-foreground"
                  )}
                >
                  <CalendarIcon className="mr-2 h-4 w-4" />
                  {dueDate ? format(new Date(dueDate), 'PPP') : 'Pick a date'}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <Calendar
                  mode="single"
                  selected={dueDate ? new Date(dueDate) : undefined}
                  onSelect={(date) => setValue('due_date', date ? format(date, 'yyyy-MM-dd') : '')}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
